import { AnalyseAppel } from "@/components/assistant/AnalyseAppel";
import { BoutonScript } from "@/components/scripts/BoutonScript";
import { SouffleurOuverture } from "@/components/scripts/Souffleur";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, PhoneOff, SkipForward, Pause, Play, Home, ArrowLeft } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, messageErreur } from "@/lib/api";
import { ResultatAppel, formatDuree } from "@/components/poste/ResultatAppel";

const CarteContact = ({ c }) => (
  <section className="at-card p-5 space-y-3 at-rise" data-testid="focus-card">
    <div className="flex items-start justify-between gap-2">
      <p className="text-xs font-bold at-pill px-2.5 py-1" style={{ background: "var(--glow)" }} data-testid="focus-reason">{c.raison}</p>
      {c.prospect.frais && <b className="text-xs" style={{ color: "var(--temp-1)" }}>Frais</b>}
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold tracking-tight break-words" data-testid="focus-name">{[c.prospect.civilite, c.prospect.prenom, c.prospect.nom].filter(Boolean).join(" ") || "Contact"}</p>
      <p className="at-muted text-sm">{c.prospect.ville}{c.prospect.ville?.includes(c.prospect.code_postal) ? "" : ` · ${c.prospect.code_postal || ""}`}</p>
    </div>
    <p className="at-soft p-3 text-sm font-semibold leading-snug" data-testid="focus-preparation">{c.preparation}</p>
    <p className="text-xs at-muted flex flex-wrap gap-x-3"><span>{c.prospect.source}</span><span>{c.prospect.tentatives} tentative(s)</span>{c.prospect.nb_biens > 1 && <span className="inline-flex items-center gap-1"><Home size={11} />{c.prospect.nb_biens} biens</span>}</p>
  </section>
);

const EnAppel = ({ appel, fin, onRaccrocher, onAppliquer }) => (
  <section className="at-card p-5 text-center space-y-3" data-testid="focus-calling">
    {!fin ? (
      <>
        <span className="mx-auto w-16 h-16 at-pill flex items-center justify-center animate-pulse" style={{ background: "var(--micro)", color: "var(--micro-ink)" }}><Phone size={26} /></span>
        <p className="font-bold">Appel en cours… <span className="at-muted font-normal">(simulation)</span></p>
        <p className="at-muted text-sm">{appel?.numero}</p>
        <button type="button" onClick={onRaccrocher} className="at-btn at-btn-danger w-full" data-testid="focus-hangup"><PhoneOff size={16} />Raccrocher</button>
      </>
    ) : (
      <div className="text-left space-y-2" data-testid="focus-call-ended">
        <p className="font-bold">{appel.decroche ? "Décroché" : "Pas de réponse"} · {formatDuree(appel.duree_s)}</p>
        {appel.transcription && <details className="at-soft p-3 text-sm"><summary className="font-semibold min-h-[44px] flex items-center cursor-pointer" data-testid="focus-transcript-toggle">Transcription (exemple)</summary><p className="whitespace-pre-line mt-2" data-testid="focus-transcript">{appel.transcription}</p></details>}
        {appel.analyse_ia && <AnalyseAppel an={appel.analyse_ia} onAppliquer={onAppliquer} />}
      </div>
    )}
  </section>
);

export default function ModeFocus() {
  const [params] = useSearchParams();
  const [carte, setCarte] = useState(undefined);
  const [statuts, setStatuts] = useState([]);
  const [auto, setAuto] = useState(true);
  const [appel, setAppel] = useState(null);
  const [fin, setFin] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [compte, setCompte] = useState(null);
  const [exclure, setExclure] = useState([]);
  const minuteur = useRef(null);

  const charger = useCallback((ex = [], pid = null) => api.post("/poste/prochaine", { exclure: ex, prospect_id: pid }).then((r) => { setCarte(r.data); setAppel(null); setFin(false); return r.data; })
    .catch((e) => { toast.error(messageErreur(e)); setCarte(null); return null; }), []);

  useEffect(() => {
    api.get("/statuts-appel").then((r) => setStatuts(r.data.statuts.filter((s) => s.code !== "nouveau")));
    api.get("/poste/accueil").then((r) => setAuto(r.data.focus_auto));
    charger([], params.get("prospect"));
  }, [charger, params]);
  useEffect(() => () => clearTimeout(minuteur.current), []);

  const terminer = () => { clearTimeout(minuteur.current); setFin(true); };
  const appeler = async (c = carte) => {
    if (!c?.prospect) return;
    setCompte(null);
    try {
      const { data } = await api.post(`/poste/appeler/${c.prospect.id}`);
      setAppel(data); setFin(false);
      minuteur.current = setTimeout(() => setFin(true), data.decroche ? 2600 : 1600);
    } catch (e) { toast.error(messageErreur(e)); }
  };

  useEffect(() => {
    if (compte === null) return undefined;
    if (compte <= 0) { appeler(); return undefined; }
    const t = setTimeout(() => setCompte(compte - 1), 1000);
    return () => clearTimeout(t);
  }, [compte]); // eslint-disable-line react-hooks/exhaustive-deps

  const valider = async (corps) => {
    setEnvoi(true);
    try {
      const { data } = await api.post(`/poste/appels/${appel.id}/resultat`, corps);
      toast.success(data.fiche_id ? "Opportunité créée dans « Lead à traiter »." : data.prochain_appel_at && data.type === "rappel" ? `Rappel planifié le ${new Date(data.prochain_appel_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}.` : data.texte);
      const suivante = await charger(exclure);
      if (auto && suivante?.prospect) setCompte(3);
    } catch (e) { toast.error(messageErreur(e)); }
    setEnvoi(false);
  };
  const passer = () => { const ex = [...exclure, carte.prospect.id]; setExclure(ex); setCompte(null); charger(ex); };
  const basculer = (v) => { setAuto(v); if (!v) setCompte(null); api.patch("/poste/reglages", { focus_auto: v }).catch((e) => toast.error(messageErreur(e))); };

  return (
    <div className="max-w-xl mx-auto min-w-0 space-y-4" data-testid="page-focus">
      <div className="flex items-center justify-between gap-2">
        <Link to="/" className="at-icon-btn shrink-0" aria-label="Retour à l'accueil" data-testid="focus-back"><ArrowLeft size={18} /></Link>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex-1" data-testid="page-title">Mode focus</h1>
      </div>
      <label className="at-card px-4 min-h-[56px] flex items-center justify-between gap-3 text-sm font-semibold" data-testid="focus-auto-row">
        Appeler le suivant automatiquement
        <Switch checked={auto} onCheckedChange={basculer} className="data-[state=checked]:bg-[var(--success)] data-[state=unchecked]:bg-[var(--line)]" data-testid="focus-auto-switch" />
      </label>
      {carte === undefined && <p className="at-muted" data-testid="focus-loading">Recherche du prochain contact…</p>}
      {carte && !carte.prospect && <p className="at-card p-5 font-semibold" data-testid="focus-empty">Plus aucun contact à appeler pour le moment. Bravo !</p>}
      {carte?.prospect && (
        <>
          <CarteContact c={carte} />
          <div className="flex justify-end"><BoutonScript operation={carte.prospect.operation_ciblee || carte.fichier?.operation} metier="agent" contact={[carte.prospect.civilite, carte.prospect.nom].filter(Boolean).join(" ")} testId="focus-script-button" /></div>
          {!appel && <SouffleurOuverture key={carte.prospect.id} url={`/souffleur/prospect/${carte.prospect.id}`} testId="focus-prompter-open" />}
          {!appel && compte === null && (
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <button type="button" onClick={() => appeler()} className="at-btn at-btn-primary w-full" data-testid="focus-call"><Phone size={16} />Appeler {carte.prospect.telephone}</button>
              <button type="button" onClick={passer} className="at-btn at-btn-ghost" aria-label="Passer ce contact" data-testid="focus-skip"><SkipForward size={16} /></button>
            </div>
          )}
          {compte !== null && (
            <div className="at-card p-4 flex items-center justify-between gap-3" data-testid="focus-countdown">
              <p className="font-semibold">Appel automatique dans <b data-testid="focus-countdown-value">{compte}</b> s</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setCompte(null)} className="at-icon-btn" aria-label="Mettre en pause" data-testid="focus-pause"><Pause size={16} /></button>
                <button type="button" onClick={() => appeler()} className="at-icon-btn" aria-label="Appeler maintenant" data-testid="focus-call-now"><Play size={16} /></button>
              </div>
            </div>
          )}
          {appel && <EnAppel appel={appel} fin={fin} onRaccrocher={terminer} onAppliquer={() => { const an = appel.analyse_ia; if (an.statut_propose === "opportunite") { if (an.operation) return valider({ statut: an.statut_propose, operation: an.operation, commentaire: `Résumé IA : ${an.resume}` }); return toast("Choisissez l'opération ci-dessous dans « Création d'opportunité »."); } valider({ statut: an.statut_propose, rappel_le: an.rappel_le || undefined, commentaire: `Résumé IA : ${an.resume}` }); }} />}
          {appel && fin && <ResultatAppel key={appel.id} statuts={statuts} decroche={appel.decroche} onValider={valider} enCours={envoi} />}
        </>
      )}
    </div>
  );
}
